import { inject, Injectable } from '@angular/core';
import { HttpEvent, HttpHeaders } from '@angular/common/http';
import { ToastrService } from 'ngx-toastr';
import { catchError, EMPTY, map, Observable } from 'rxjs';
import { ApiCallingService } from './api-calling.service';

@Injectable({
  providedIn: 'root'
})
export class FileUploadService {
  #http = inject(ApiCallingService);
  #toastr = inject(ToastrService);
  recording: boolean = false;

  getAWSSignedUrl(file: File): Observable<{ fileName: string; presignedUrl: string; s3Key: string; sourceFile: File }> {
    const body = JSON.stringify(file.name);
    return this.#http.postData('documents', 'private/upload-url', body, true).pipe(
      map(
        (response: any) => ({
          fileName: body,
          presignedUrl: response.data.uploadUrl,
          s3Key: response.data.s3Key,
          sourceFile: file
        }),
        catchError(error => this.handleError(error))
      )
    );
  }

  getPrivateAWSSignedUrl(
    file: File
  ): Observable<{ fileName: string; presignedUrl: string; s3Key: string; sourceFile: File; documentId: string }> {
    const body = JSON.stringify(file.name);
    const options = { headers: new HttpHeaders({ 'Content-Type': 'application/json' }) };
    return this.#http.postData('documents', 'private/upload-url', body, true, options).pipe(
      map(
        (response: any) => ({
          fileName: body,
          presignedUrl: response.data.uploadUrl,
          s3Key: response.data.s3Key,
          documentId: response.data.documentId,
          sourceFile: file
        }),
        catchError(error => this.handleError(error))
      )
    );
  }
  generatePublicUploadUrl(fileName?: string): Observable<{ documentId: string; s3Key: string; uploadUrl: string }> {
    // Backend expects [FromBody] string? fileName. To avoid 415, send as JSON string with application/json
    const body = fileName !== undefined && fileName !== null ? JSON.stringify(fileName) : null;
    const options = { headers: new HttpHeaders({ 'Content-Type': 'application/json' }) };
    return this.#http.postData('documents', 'public/upload-url', body, true, options).pipe(
      map((res: any) => {
        if (!res.success) throw res;
        return res.data as { documentId: string; s3Key: string; uploadUrl: string };
      }),
      catchError(error => this.handleError(error))
    );
  }

  generatePrivateUploadUrl(fileName?: string): Observable<{ documentId: string; s3Key: string; uploadUrl: string }> {
    // Backend expects [FromBody] string? fileName. To avoid 415, send as JSON string with application/json
    const body = fileName !== undefined && fileName !== null ? JSON.stringify(fileName) : null;
    const options = { headers: new HttpHeaders({ 'Content-Type': 'application/json' }) };
    return this.#http.postData('documents', 'private/upload-url', body, true, options).pipe(
      map((res: any) => {
        if (!res.success) throw res;
        return res.data as { documentId: string; s3Key: string; uploadUrl: string };
      }),
      catchError(error => this.handleError(error))
    );
  }

  // Get presigned URL from filesystem for an existing S3 object
  getPresignedUrlFromS3Key(s3Key: string, fileName: string): Observable<string> {
    return this.#http.getData('filesystem', `file/presigned-url?s3Key=${s3Key}&fileName=${fileName}`, true).pipe(
      map((res: any) => {
        if (!res.success) throw res;
        return res.data.presignedUrl;
      }),
      catchError(error => this.handleError(error))
    );
  }

  uploadFileToS3(fileObject: {
    fileName: string;
    presignedUrl: string;
    s3Key: string;
    sourceFile: File;
  }): Observable<{ fileName: string; presignedUrl: string; s3Key: string; sourceFile: File }> {
    let file: File = fileObject.sourceFile;
    return this.#http.awsFileUpload(fileObject.presignedUrl, file, true);
  }

  uploadFileToS3WithProgress(fileObject: {
    fileName: string;
    presignedUrl: string;
    s3Key: string;
    sourceFile: File;
  }): Observable<HttpEvent<unknown>> {
    const file = fileObject.sourceFile;
    return this.#http.awsFileUpload(fileObject.presignedUrl, file, true);
  }

  uploadFileToS3WithFetch(fileObject: {
    fileName: string;
    presignedUrl: string;
    s3Key: string;
    sourceFile: File;
  }): Observable<{ fileName: string; presignedUrl: string; s3Key: string; sourceFile: File }> {
    let file: File = fileObject.sourceFile;
    return this.#http.awsFileUploadWithFetch(fileObject.presignedUrl, file, true);
  }

  private handleError(error: any) {
    const errorMessage = error.message || 'An error occurred';
    this.#toastr.error(errorMessage);
    return EMPTY;
    // return throwError(() => error);
  }
}
